/**
 * Leituras (GET) que caem por falha de rede no celular (troca de 4G/5G para
 * Wi-Fi, sinal fraco) são repetidas sozinhas antes de mostrar erro.
 *
 * Visto em 30/09/2026: no celular do Reinaldo a lista da Carteira nunca chegou
 * ao servidor e a tela ficou em "—" até o navegador desistir com
 * "Network Error". O servidor respondia tudo em menos de 0,5 s.
 *
 * Só repete quando não houve resposta do servidor (erro de rede ou tempo
 * esgotado). Resposta com status (401, 403, 500...) nunca é repetida aqui.
 */

/** Tempo máximo de uma leitura antes de desistir e tentar de novo. */
export const TEMPO_LIMITE_DA_LEITURA_MS = 30_000;

/** Esperas entre as tentativas: 1 s e depois 3 s (três tentativas no total). */
export const ESPERAS_ENTRE_TENTATIVAS_MS = [1_000, 3_000];

type ErroDeRequisicao = {
  response?: unknown;
  code?: string;
  config?: { signal?: AbortSignal };
  name?: string;
};

/** Falhou sem resposta do servidor (rede caiu ou tempo esgotado), e não foi cancelada. */
export const ehFalhaDeRede = (erro: unknown): boolean => {
  const e = erro as ErroDeRequisicao | null | undefined;
  if (!e || e.response) return false;
  if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return false;
  if (e.config?.signal?.aborted) return false;
  return e.code === "ERR_NETWORK" || e.code === "ECONNABORTED" || e.code === "ETIMEDOUT" || e.name === "AxiosError";
};

const esperar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Executa a leitura e repete em caso de falha de rede, com esperas crescentes. */
export async function comRetentativa<T>(
  leitura: () => Promise<T>,
  esperas: number[] = ESPERAS_ENTRE_TENTATIVAS_MS,
  aguardar: (ms: number) => Promise<unknown> = esperar,
): Promise<T> {
  for (let tentativa = 0; ; tentativa += 1) {
    try {
      return await leitura();
    } catch (erro) {
      if (tentativa >= esperas.length || !ehFalhaDeRede(erro)) throw erro;
      await aguardar(esperas[tentativa]);
    }
  }
}
